import React, { useRef, useEffect, useState, Fragment } from "react";
import PropTypes from "kf-proptypes/widgets";
import { t } from "@lingui/macro";
import {
  NimblePicker,
  NimbleEmojiIndex,
  Emoji as NimbleEmoji
} from "emoji-mart";

import { useAccountStore } from "base/stores";
import { DataService } from "base/services";
import { debounce } from "base/utils";

import { Icon } from "@widgets/common/components/primitives/icon/index.jsx";
import { SEARCH_SIZES, SearchInput } from "../search/index.jsx";
import { EmojiDrawer } from "./emojidrawer.jsx";
import {
  EmptyState,
  EMPTY_STATE_SIZES
} from "../../layouts/emptystate/index.jsx";
import { InfiniteScroll } from "@widgets/others/infinitescroll/index.jsx";
import {
  KF_ILLUSTRATIONS,
  KfIllustration
} from "@widgets/common/components/primitives/kf_icon/index.jsx";

import data from "emoji-mart/data/all.json";

import styles from "./emojipicker.css";

import SmileIcon from "./assets/Smile.svg";
import GifIcon from "./assets/Gif.svg";
import NoItemFoundIcon from "./assets/NoItemFound.svg";
import "emoji-mart/css/emoji-mart.css";
import "./emojipicker.override.css";

let emojiIndex = new NimbleEmojiIndex(data);

const GIF_VENDOR = "Tenor";

const EmojiPickerService = (params) => {
  return DataService.get(`/global/2/${params.accountId}/gif`, {
    query: { q: params.text }
  });
};

function filterEmoji(emoji) {
  if (["White Smiling Face", "White Frowning Face"].includes(emoji.name)) {
    return false;
  }
  return true;
}

EmojiPicker.propTypes = {
  icon: PropTypes.bool,
  className: PropTypes.string,
  onClick: PropTypes.func,
  position: PropTypes.string,
  containerClassName: PropTypes.string,
  paddingNone: PropTypes.bool,
  highlight: PropTypes.bool,
  onlyEmoji: PropTypes.bool,
  isPopoverRequired: PropTypes.bool,
  iconType: PropTypes.string,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  native: PropTypes.bool,
  shouldReturnEmojiObject: PropTypes.bool,
  open: PropTypes.bool
};

export function EmojiPicker(props) {
  const {
    icon: EmojiIcon = <SmileIcon fill={"var(--kf-color-gray-700)"} />,
    className,
    onClick,
    position,
    containerClassName,
    paddingNone,
    highlight,
    onlyEmoji,
    isPopoverRequired,
    iconType,
    onOpen,
    onClose,
    native,
    shouldReturnEmojiObject,
    open
  } = props;
  const [isOpen, setIsOpen] = useState(false);

  useEffect(
    function togglePopup() {
      setIsOpen(open);
    },
    [open]
  );

  function onClickHandler(value, emojiObject) {
    if (shouldReturnEmojiObject) {
      onClick(value, emojiObject);
      return;
    }
    onClick(value);
  }

  return onlyEmoji && isPopoverRequired ? (
    <EmojiDrawer
      position={position}
      onOpen={() => {
        setIsOpen(true);
        onOpen && onOpen();
      }}
      onClose={() => {
        setIsOpen(false);
        onClose && onClose();
      }}
      isOpen={isOpen}
      targetRenderer={() => {
        return (
          <Icon
            className={className}
            paddingNone={paddingNone}
            highlight={highlight}
            focussed={isOpen}
            clickable={highlight}
            type={iconType}
          >
            {EmojiIcon}
          </Icon>
        );
      }}
    >
      <EmojiPickerContainer
        onClick={onClickHandler}
        className={containerClassName}
        native={native}
      />
    </EmojiDrawer>
  ) : (
    <EmojiPickerContainer
      onClick={onClickHandler}
      className={containerClassName}
    />
  );
}

EmojiPickerContainer.propTypes = {
  onClick: PropTypes.func,
  className: PropTypes.string,
  native: PropTypes.bool,
  onEmojiClick: PropTypes.func,
  searchInputSize: PropTypes.string,
  emptyStateConfig: PropTypes.shape({
    size: PropTypes.string,
    img: PropTypes.string,
    className: PropTypes.string
  })
};

export function EmojiPickerContainer(props) {
  const {
    onClick,
    className = "",
    native = true,
    searchInputSize = SEARCH_SIZES.BASE,
    emptyStateConfig = {
      size: EMPTY_STATE_SIZES.SMALL,
      img: KF_ILLUSTRATIONS.NOTHING_FOUND,
      className: styles.emptystate
    }
  } = props;
  const pickerRef = useRef();
  const inputRef = useRef();
  const categories = useRef({
    search: t`Search results`,
    recent: t`Most popular`,
    people: t`Smileys & People`,
    nature: t`Animals & Nature`,
    foods: t`Food & Drink`,
    activity: t`Activity`,
    places: t`Travel & Places`,
    objects: t`Objects`,
    symbols: t`Symbols`,
    flags: t`Flags`
  });
  function handleEmojiSearch(e) {
    const value = (e.target && e.target.value) || e;
    pickerRef.current.handleSearch(
      emojiIndex.search(value, {
        emojisToShowFilter: filterEmoji
      })
    );
  }

  useEffect(function initEffect() {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, []);

  const notFound = () => (
    <div className={emptyStateConfig.className}>
      <EmptyState
        size={emptyStateConfig.size}
        img={<KfIllustration icon={emptyStateConfig.img} />}
        text={t`No matching results found`}
      />
    </div>
  );

  return (
    <div className={`${styles.emojicontainer} ${className} `}>
      <div className={styles.emojiinput}>
        <SearchInput
          ref={inputRef}
          onChange={handleEmojiSearch}
          placeholder={t`Search by name or category`}
          size={searchInputSize}
          onClear={() => handleEmojiSearch("")}
        />
      </div>
      <div className={styles.emojidrawer}>
        <NimblePicker
          data={data}
          ref={pickerRef}
          native={native}
          emojiSize={24}
          perLine={10}
          darkMode={false}
          onClick={(e) => {
            onClick(e.native, { _id: e.id });
          }}
          emojisToShowFilter={filterEmoji}
          skin={1}
          theme={"light"}
          recent={[
            "smiley",
            "grinning",
            "thumbsup",
            "heart_eyes",
            "joy",
            "sweat_smile",
            "scream",
            "laughing"
          ]}
          i18n={{
            categories: categories.current
          }}
          style={{ float: "left", width: "100%" }}
          notFound={notFound}
        />
      </div>
    </div>
  );
}

GifPicker.propTypes = {
  icon: PropTypes.bool,
  className: PropTypes.string,
  onClick: PropTypes.func,
  position: PropTypes.string,
  containerClassName: PropTypes.string,
  paddingNone: PropTypes.bool,
  highlight: PropTypes.bool,
  onlyGif: PropTypes.bool,
  isPopoverRequired: PropTypes.bool,
  iconType: PropTypes.string
};

export function GifPicker(props) {
  const {
    icon: GifIconComponent = <GifIcon fill={"var(--kf-color-gray-700)"} />,
    className,
    onClick,
    position,
    containerClassName,
    paddingNone,
    highlight,
    onlyGif,
    isPopoverRequired,
    iconType
  } = props;

  const [isOpen, setIsOpen] = useState(false);

  function onClickHandler(value, isGif, gifId) {
    if (onlyGif && isPopoverRequired) setIsOpen(false);
    onClick(value, isGif, gifId);
  }

  return onlyGif && isPopoverRequired ? (
    <EmojiDrawer
      position={position}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      isOpen={isOpen}
      targetRenderer={() => {
        return (
          <Icon
            className={className}
            paddingNone={paddingNone}
            highlight={highlight}
            focussed={isOpen}
            clickable={highlight}
            type={iconType}
          >
            {GifIconComponent}
          </Icon>
        );
      }}
    >
      <RenderGIF onClick={onClickHandler} className={containerClassName} />
    </EmojiDrawer>
  ) : (
    <RenderGIF onClick={onClickHandler} className={containerClassName} />
  );
}

function RenderGIF(prop) {
  const { onClick, className } = prop;

  const gifList = useRef([]);
  const [gifData, setGifData] = useState([]);
  const tempRef = useRef([]);
  const isInitialCallMade = useRef();
  const inputRef = useRef();

  useEffect(
    function initEffect() {
      tempRef.current = gifData;
    },
    [gifData]
  );

  useEffect(function loadinitialGif() {
    getData();
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handlePagination(start, end, reset = false) {
    const data = gifList.current.slice(start, end);
    if (!reset) setGifData((state) => [...state, ...data]);
    else setGifData([...data]);
  }

  async function getData(text = "") {
    const params = {
      accountId: useAccountStore.getState()._id,
      text
    };
    const [promise] = EmojiPickerService(params);
    const response = await promise();
    isInitialCallMade.current = true;
    gifList.current = response;
    handlePagination(0, 10, true);
  }

  const handleSearch = debounce(function handleSearch(value) {
    if (value) getData(value);
    else getData();
  }, 500);

  function loadNextData() {
    const pageLength = tempRef.current.length / 10;
    const start = Math.ceil(pageLength * 10);
    const end = Math.ceil((pageLength + 1) * 10);
    handlePagination(start, end);
  }

  function onSelectHandler(gif) {
    const originalSizeinKb = Math.trunc(gif?.Original?.size / 1024);
    const mediumSizeinKb = Math.trunc(gif?.Medium?.size / 1024);
    let gifUrl = gif.Tiny?.url;
    if (originalSizeinKb < 500) {
      gifUrl = gif.Original?.url;
    } else if (mediumSizeinKb < 500) {
      gifUrl = gif.Medium?.url;
    }
    onClick(gifUrl, true, gif._id);
  }

  return (
    <div className={`${styles.gifMaincontainer} ${className}`}>
      {!isInitialCallMade.current ? (
        <SkeletonLoader />
      ) : (
        <Fragment>
          <div className={styles.emojiinput}>
            <SearchInput
              ref={inputRef}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={t`Search GIFs via ${GIF_VENDOR}`}
              onClear={() => handleSearch()}
            />
          </div>
          <InfiniteScroll
            onEndReached={() => {
              loadNextData();
            }}
            style={{
              height: gifData.length > 0 ? "calc(100% - 5.6rem)" : "inherit"
            }}
          >
            <div
              className={`${styles.gifcontainer} ${
                gifData.length === 0 ? styles.emptygif : ""
              }`}
            >
              {gifData.length === 0 && isInitialCallMade.current ? (
                <div className={styles.emptystate}>
                  <EmptyState
                    size={EMPTY_STATE_SIZES.SMALL}
                    img={<NoItemFoundIcon />}
                    text={t`Couldn’t find a GIF`}
                  />
                </div>
              ) : (
                gifData.map((url) => {
                  return (
                    <img
                      src={url?.Nano?.url}
                      alt={"gifimage"}
                      key={url?.Nano?.url}
                      onClick={() => onSelectHandler(url)}
                      loading="lazy"
                    />
                  );
                })
              )}
            </div>
          </InfiniteScroll>
        </Fragment>
      )}
    </div>
  );
}

function SkeletonLoader() {
  return (
    <Fragment>
      <div className={`base ${styles.skeletoninput}`}>
        <p className="skeleton-loader" />
      </div>
      <div className={`base ${styles.gifskeletonloader}`}>
        <div>
          <p className="skeleton-loader">
            Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
            eiusmod tempor incididunt ut labore et dolore magna aliqua.
          </p>
        </div>
        <div>
          <p className="skeleton-loader">
            Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
            eiusmod tempor incididunt ut labore et dolore magna aliqua.
          </p>
        </div>
        <div>
          <p className="skeleton-loader">
            Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
            eiusmod tempor incididunt ut labore et dolore magna aliqua.
          </p>
        </div>
        <div>
          <p className="skeleton-loader">
            Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
            eiusmod tempor incididunt ut labore et dolore magna aliqua.
          </p>
        </div>
        <div>
          <p className="skeleton-loader">
            Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
            eiusmod tempor incididunt ut labore et dolore magna aliqua.
          </p>
        </div>
        <div>
          <p className="skeleton-loader">
            Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
            eiusmod tempor incididunt ut labore et dolore magna aliqua.
          </p>
        </div>
      </div>
    </Fragment>
  );
}

Emoji.propTypes = {
  src: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  size: PropTypes.number
};

export function Emoji(props) {
  const { size = 16, src } = props;
  return <NimbleEmoji emoji={src} size={size} />;
}
